import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { ITeam } from '@core/interfaces/team.interface';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { filter } from 'rxjs';
import { ISelectTeamDialogData, TSelectTeamDialogCloseResult } from './select-team-dialog.interface';

/**
 * Lists every team from `GET /teams`, each row showing its icon (via IconBadgeComponent, falling
 * back to a badge derived from the team's name) and name. Same rules as SelectRoleDialogComponent
 * (including Save owning its own request lifecycle via `data.onSave` - see that component's doc
 * comment): not closable via backdrop/ESC (`disableClose: true` set by SelectTeamDialogService),
 * and the explicit close (X) / Cancel controls show a discard-confirmation if the selection
 * changed.
 *
 * Always open this via SelectTeamDialogService, never directly - the service also owns the
 * "Add team" round-trip (see that service's doc comment).
 */
@Component({
  imports: [
    DotsSpinnerComponent,
    HasPermissionDirective,
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    SideDialogComponent,
    TranslateModule,
  ],
  selector: 'app-select-team-dialog',
  templateUrl: './select-team-dialog.component.html',
})
export class SelectTeamDialogComponent {
  public readonly EPermissions = EPermissions;

  public readonly data = inject<ISelectTeamDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SelectTeamDialogComponent, TSelectTeamDialogCloseResult>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  public readonly selectedTeamId = signal<string | null>(this.data.currentTeamId);
  public readonly hasChanged = computed(() => this.selectedTeamId() !== this.data.savedTeamId);
  public readonly submitLoading = signal<boolean>(false);

  /** Teams are optional (unlike roles) - clicking the already-selected team deselects it. */
  public onTeamClick(team: ITeam): void {
    if (this.submitLoading()) {
      return;
    }

    this.selectedTeamId.set(this.selectedTeamId() === team.id ? null : team.id);
  }

  public onAddTeamClick(): void {
    if (this.submitLoading()) {
      return;
    }

    this.dialogRef.close({ selectedTeamId: this.selectedTeamId(), type: 'addTeam' });
  }

  public onSaveClick(): void {
    if (this.submitLoading()) {
      return;
    }

    const teamId = this.selectedTeamId();

    if (teamId === this.data.savedTeamId) {
      this.dialogRef.close({ teamId, type: 'save' });

      return;
    }

    this.submitLoading.set(true);

    this.data.onSave(teamId).subscribe({
      error: () => {
        this.submitLoading.set(false);
      },
      next: () => {
        this.submitLoading.set(false);
        this.dialogRef.close({ teamId, type: 'save' });
      },
    });
  }

  public onCloseClick(): void {
    if (this.submitLoading()) {
      return;
    }

    if (!this.hasChanged()) {
      this.dialogRef.close(undefined);

      return;
    }

    const confirmRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.cancel'),
        confirmText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.confirm'),
        message: this.translate.instant('pages.settings.userManagement.discardChangesDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.settings.userManagement.discardChangesDialog.title'),
      } as IConfirmDialogData,
      width: '400px',
    });

    confirmRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.dialogRef.close(undefined));
  }
}
